/**
 * usePhaseAccess Hook
 * 
 * Provides phase-based access checking utilities.
 * This hook layers phase-based constraints on top of role-based access.
 * 
 * IMPORTANT: parkProAdmin users bypass all phase restrictions and have access to all features.
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { isParkProAdmin } from '../utils/roleUtils';
import { isRouteInPhase, getRoutePhase, getPhaseName, getNextPhaseName, ACTIVE_PHASE } from '../config/rolloutConfig';

/**
 * Hook to check phase-based access for current route or specified route
 * @param {string|null} route - Optional route path (defaults to current location)
 * @returns {Object} Phase access information
 */
export const usePhaseAccess = (route = null) => {
  const location = useLocation();
  const { user } = useUser();
  const currentRoute = route || location.pathname;
  
  // parkProAdmin users bypass all phase restrictions
  const isAdmin = isParkProAdmin(user?.role);
  
  const access = useMemo(() => {
    // If user is parkProAdmin, always allow access regardless of phase
    const isAvailable = isAdmin ? true : isRouteInPhase(currentRoute);
    const requiredPhase = getRoutePhase(currentRoute);
    const nextPhase = requiredPhase ? getNextPhaseName(requiredPhase) : null;
    
    return {
      isAvailable,
      requiredPhase,
      requiredPhaseName: requiredPhase ? getPhaseName(requiredPhase) : null,
      nextPhase,
      currentPhase: ACTIVE_PHASE,
      currentPhaseName: getPhaseName(ACTIVE_PHASE),
      bypassedByAdmin: isAdmin && requiredPhase !== null, // Track if admin bypass was used
    };
  }, [currentRoute, isAdmin]);
  
  return access;
};

/**
 * Check if a specific route is available in current phase
 * @param {string} route - Route path
 * @returns {boolean} True if route is available
 */
export const useIsRouteAvailable = (route) => {
  const { user } = useUser();
  const access = usePhaseAccess(route);
  
  // parkProAdmin users always have access
  if (isParkProAdmin(user?.role)) {
    return true;
  }
  
  return access.isAvailable;
};
